import React, { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    Box,
    Typography,
    CircularProgress,
    Alert,
    DialogActions,
    Autocomplete,
    Grid,
    IconButton,
    Card,
    CardContent
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RouteIcon from '@mui/icons-material/Route';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import InventoryIcon from '@mui/icons-material/Inventory';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import { DesktopDateTimePicker } from '@mui/x-date-pickers/DesktopDateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { createTrip } from '../../../features/tripcenter/tripsApi';
import { assetNamesSelector } from '../../../features/assets/assetsSlice';
import { creatingTripSelector, creatingTripErrorSelector, clearTripCreationError } from '../../../features/tripcenter/tripControlSlice';
import { selectRouteListAll, selectRouteListLoaded } from '../../../features/tripcenter/routeSlice';
import { getRouteList } from '../../../features/tripcenter/routeApi';

const NewTrip = ({ open, onClose, initialRoute, initialTruck }) => {
    const dispatch = useDispatch();
    const assetNames = useSelector(assetNamesSelector);
    const routeList = useSelector(selectRouteListAll);
    const routeListLoaded = useSelector(selectRouteListLoaded);
    const creatingTrip = useSelector(creatingTripSelector);
    const creatingTripError = useSelector(creatingTripErrorSelector);

    // Prepare options for dropdowns
    const assetOptions = assetNames ? Object.keys(assetNames).map(key => ({
        id: key,
        value: key,
        label: assetNames[key]?.regn || '',
        regn: assetNames[key]?.regn || ''
    })) : [];

    const routeNameOptions = routeList ? [...new Set(routeList.map(route => route.routeName))]
        .map(name => ({ id: name, label: name })) : [];

    const [formData, setFormData] = useState({
        // Route section
        routeName: initialRoute || '',
        routeNumber: '',
        tripStartTime: dayjs().add(1, 'hour').minute(Math.ceil(dayjs().minute() / 30) * 30),
        externalTripId: '',
        
        // Truck & Driver
        vehicleImei: initialTruck?.deviceImei || '',
        truckRegistrationNumber: initialTruck?.truckRegistrationNumber || '',
        driverName: '',
        driverPhone: '',
        
        // Customer & Goods
        customerName: '',
        goodsName: '',
        goodsDescription: '',
        
        // Auto-generated
        tripName: ''
    });

    const [errors, setErrors] = useState({
        routeName: false,
        tripStartTime: false,
        vehicleImei: false,
        driverName: false,
        driverPhone: false,
        customerName: false,
        goodsName: false,
        tripName: false
    });

    const [isFormValid, setIsFormValid] = useState(false);
    const [isRouteEditMode, setIsRouteEditMode] = useState(!initialRoute);
    const [isTruckEditMode, setIsTruckEditMode] = useState(!initialTruck);

    // Load routes if not already loaded
    useEffect(() => {
        if (routeList?.length === 0 && !routeListLoaded) {
            dispatch(getRouteList());
        }
    }, [dispatch, routeList, routeListLoaded]);

    // Update formData when initialRoute or initialTruck changes
    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            routeName: initialRoute || '',
            vehicleImei: initialTruck?.deviceImei || '',
            truckRegistrationNumber: initialTruck?.truckRegistrationNumber || ''
        }));
        setIsRouteEditMode(!initialRoute);
        setIsTruckEditMode(!initialTruck);
    }, [initialRoute, initialTruck]);

    // Auto-generate trip name
    const computeTripName = useCallback(() => {
        if (!formData.routeName || !formData.truckRegistrationNumber || !formData.tripStartTime) {
            return '';
        }

        const routeName = formData.routeName;
        const plannedDate = dayjs(formData.tripStartTime).format('DMMM').toUpperCase();

        return `${routeName}|${formData.truckRegistrationNumber}|${plannedDate}`;
    }, [formData.routeName, formData.truckRegistrationNumber, formData.tripStartTime]);

    useEffect(() => {
        const computedName = computeTripName();
        if (computedName) {
            setFormData(prev => ({
                ...prev,
                tripName: computedName
            }));
        }
    }, [computeTripName]);

    // Form validation
    const validateForm = useCallback((formDataToValidate = formData) => {
        const phoneNumberPattern = /^\d{10}$/;

        const newErrors = {
            routeName: !formDataToValidate.routeName,
            tripStartTime: !formDataToValidate.tripStartTime,
            vehicleImei: !formDataToValidate.vehicleImei,
            driverName: !formDataToValidate.driverName,
            driverPhone: formDataToValidate.driverPhone !== '' &&
                !phoneNumberPattern.test(formDataToValidate.driverPhone),
            customerName: !formDataToValidate.customerName,
            goodsName: !formDataToValidate.goodsName,
            tripName: !formDataToValidate.tripName
        };

        setErrors(newErrors);
        const valid = !Object.values(newErrors).some(error => error);
        setIsFormValid(valid);
        return valid;
    }, [formData]);

    useEffect(() => {
        validateForm();
    }, [formData, validateForm]);

    // Handle form changes
    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleRouteNameChange = (event, newValue) => {
        event?.preventDefault?.();
        const selectedRoute = routeList.find(route => route.routeName === newValue?.label);
        setFormData(prev => ({
            ...prev,
            routeName: newValue?.label || '',
            routeNumber: selectedRoute?.routeNumber || ''
        }));
    };

    const handleRouteEditClick = () => {
        setIsRouteEditMode(true);
    };

    const handleRouteEditSave = () => {
        setIsRouteEditMode(false);
    };

    const handleRouteEditCancel = () => {
        setFormData(prev => ({
            ...prev,
            routeName: initialRoute || ''
        }));
        setIsRouteEditMode(false);
    };

    const handleTruckEditClick = () => {
        setIsTruckEditMode(true);
    };

    const handleTruckEditSave = () => {
        setIsTruckEditMode(false);
    };

    const handleTruckEditCancel = () => {
        setFormData(prev => ({
            ...prev,
            vehicleImei: initialTruck?.deviceImei || '',
            truckRegistrationNumber: initialTruck?.truckRegistrationNumber || ''
        }));
        setIsTruckEditMode(false);
    };

    const handleVehicleSelect = (event, newValue) => {
        event?.preventDefault?.();
        setFormData(prev => ({
            ...prev,
            vehicleImei: newValue?.value || '',
            truckRegistrationNumber: newValue?.regn || ''
        }));
    };

    const handleDateTimeChange = (newValue) => {
        const roundedDate = roundToNearest15Minutes(newValue);
        setFormData(prev => ({
            ...prev,
            tripStartTime: roundedDate
        }));
    };

    const roundToNearest15Minutes = (date) => {
        const minutes = date.minute();
        const remainder = minutes % 15;
        const roundedMinutes = remainder < 8 ? minutes - remainder : minutes + (15 - remainder);
        return date.minute(roundedMinutes).second(0);
    };

    // Reset form
    const handleResetForm = useCallback(() => {
        setFormData({
            routeName: initialRoute || '',
            routeNumber: '',
            tripStartTime: dayjs().add(1, 'hour').minute(Math.ceil(dayjs().minute() / 30) * 30),
            externalTripId: '',
            vehicleImei: initialTruck?.deviceImei || '',
            truckRegistrationNumber: initialTruck?.truckRegistrationNumber || '',
            driverName: '',
            driverPhone: '',
            customerName: '',
            goodsName: '',
            goodsDescription: '',
            tripName: ''
        });
        setErrors({});
        setIsRouteEditMode(!initialRoute);
        setIsTruckEditMode(!initialTruck);
        dispatch(clearTripCreationError());
    }, [dispatch, initialRoute, initialTruck]);

    // Handle form submission
    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!validateForm()) {
            return;
        }

        dispatch(clearTripCreationError());

        try {
            const tripPayload = {
                tripName: formData.tripName,
                route: formData.routeName,
                plannedStartTime: formData.tripStartTime.format('YYYY-MM-DDTHH:mm'),
                erpSystemId: formData.externalTripId,
                deviceImei: formData.vehicleImei,
                truckRegistrationNumber: formData.truckRegistrationNumber,
                driverName: formData.driverName,
                driverPhoneNumber: formData.driverPhone,
                customer: formData.customerName,
                goodsName: formData.goodsName,
                goodsDescription: formData.goodsDescription
            };

            await dispatch(createTrip(tripPayload)).unwrap();
            handleResetForm();
            onClose();

        } catch (error) {
            console.error('Failed to create trip:', error);
        }
    };

    // Reset form when modal closes
    useEffect(() => {
        if (!open) {
            handleResetForm();
        }
    }, [open, handleResetForm]);

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (creatingTrip) return;
                onClose();
            }}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 3,
                    px: 4
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Create New Trip
                </Typography>
                <IconButton
                    onClick={onClose}
                    disabled={creatingTrip}
                    sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {creatingTrip ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '400px',
                            gap: 2
                        }}
                    >
                        <CircularProgress size={60} />
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontSize: '1.1rem',
                                fontWeight: 500
                            }}
                        >
                            Creating Trip, please wait...
                        </Typography>
                    </Box>
                ) : creatingTripError ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            p: 4
                        }}
                    >
                        <Alert
                            severity="error"
                            onClose={() => dispatch(clearTripCreationError())}
                            sx={{ width: '100%' }}
                        >
                            {creatingTripError}
                        </Alert>
                    </Box>
                ) : (
                    <Box component="form" onSubmit={handleSubmit} sx={{ p: 4 }}>
                        <Grid container spacing={4}>
                            {/* Route Section */}
                            <Grid item xs={12}>
                                <Card sx={{ mb: 3, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <RouteIcon sx={{ color: 'primary.main' }} />
                                            Route Information
                                        </Typography>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={4}>
                                                {isRouteEditMode ? (
                                                    <Autocomplete
                                                        options={routeNameOptions}
                                                        getOptionLabel={(option) => option.label}
                                                        value={formData.routeName ? { id: formData.routeName, label: formData.routeName } : null}
                                                        onChange={handleRouteNameChange}
                                                        isOptionEqualToValue={(option, value) => option.label === value.label}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Route Selection *"
                                                                placeholder="Select route..."
                                                                error={errors.routeName}
                                                                helperText={errors.routeName ? 'Route is required' : ''}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        borderRadius: 2,
                                                                        backgroundColor: '#f8fafc',
                                                                        '&:hover': {
                                                                            backgroundColor: '#f1f5f9'
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                ) : (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            label="Selected Route"
                                                            value={formData.routeName}
                                                            InputProps={{ readOnly: true }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: 2,
                                                                    backgroundColor: '#f0f4ff',
                                                                    '& fieldset': {
                                                                        borderColor: '#667eea'
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                        <IconButton
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={handleRouteEditClick}
                                                            sx={{
                                                                borderRadius: 2,
                                                                borderColor: '#667eea',
                                                                color: '#667eea',
                                                                '&:hover': {
                                                                    borderColor: '#5a67d8',
                                                                    backgroundColor: '#f0f4ff'
                                                                }
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DesktopDateTimePicker
                                                        label="Trip Start Time *"
                                                        value={formData.tripStartTime}
                                                        onChange={handleDateTimeChange}
                                                        disablePast
                                                        minutesStep={15}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                fullWidth
                                                                error={errors.tripStartTime}
                                                                helperText={errors.tripStartTime ? 'Start time is required' : ''}
                                                                InputProps={{
                                                                    ...params.InputProps,
                                                                    startAdornment: <CalendarTodayIcon sx={{ color: 'primary.main', mr: 1 }} />
                                                                }}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        borderRadius: 2,
                                                                        backgroundColor: '#f8fafc',
                                                                        '&:hover': {
                                                                            backgroundColor: '#f1f5f9'
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </LocalizationProvider>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <TextField
                                                    fullWidth
                                                    name="externalTripId"
                                                    label="External Trip ID"
                                                    value={formData.externalTripId}
                                                    onChange={handleChange}
                                                    placeholder="Enter external trip ID..."
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                            backgroundColor: '#f8fafc',
                                                            '&:hover': {
                                                                backgroundColor: '#f1f5f9'
                                                            }
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Truck & Driver Section */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ mb: 3, borderRadius: 2, border: '1px solid #e2e8f0', height: '100%' }}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <DirectionsCarIcon sx={{ color: 'primary.main' }} />
                                            Truck & Driver
                                        </Typography>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                {isTruckEditMode ? (
                                                    <Autocomplete
                                                        options={assetOptions || []}
                                                        getOptionLabel={(option) => option?.label || ''}
                                                        value={formData.vehicleImei && assetOptions ?
                                                            assetOptions.find(opt => opt?.value === formData.vehicleImei) || null
                                                            : null
                                                        }
                                                        onChange={handleVehicleSelect}
                                                        isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Vehicle *"
                                                                placeholder="Select vehicle..."
                                                                error={errors.vehicleImei}
                                                                helperText={errors.vehicleImei ? 'Vehicle is required' : ''}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        borderRadius: 2,
                                                                        backgroundColor: '#f8fafc',
                                                                        '&:hover': {
                                                                            backgroundColor: '#f1f5f9'
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                ) : (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            label="Selected Vehicle"
                                                            value={formData.truckRegistrationNumber}
                                                            InputProps={{ readOnly: true }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: 2,
                                                                    backgroundColor: '#f0f4ff',
                                                                    '& fieldset': {
                                                                        borderColor: '#667eea'
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={handleTruckEditClick}
                                                            startIcon={<EditIcon />}
                                                            sx={{
                                                                borderRadius: 2,
                                                                borderColor: '#667eea',
                                                                color: '#667eea',
                                                                '&:hover': {
                                                                    borderColor: '#5a67d8',
                                                                    backgroundColor: '#f0f4ff'
                                                                }
                                                            }}
                                                        >
                                                            Edit
                                                        </Button>
                                                    </Box>
                                                )}
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    name="driverName"
                                                    label="Driver Name *"
                                                    value={formData.driverName}
                                                    onChange={handleChange}
                                                    error={errors.driverName}
                                                    helperText={errors.driverName ? 'Driver name is required' : ''}
                                                    placeholder="Enter driver name..."
                                                    InputProps={{
                                                        startAdornment: <PersonIcon sx={{ color: 'primary.main', mr: 1 }} />
                                                    }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                            backgroundColor: '#f8fafc',
                                                            '&:hover': {
                                                                backgroundColor: '#f1f5f9'
                                                            }
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    name="driverPhone"
                                                    label="Driver Phone"
                                                    value={formData.driverPhone}
                                                    onChange={handleChange}
                                                    error={errors.driverPhone}
                                                    helperText={errors.driverPhone ? 'Phone number must be 10 digits' : 'Optional - 10 digit number'}
                                                    placeholder="Enter 10-digit phone number..."
                                                    type="tel"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                            backgroundColor: '#f8fafc',
                                                            '&:hover': {
                                                                backgroundColor: '#f1f5f9'
                                                            }
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Customer & Goods Section */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ mb: 3, borderRadius: 2, border: '1px solid #e2e8f0', height: '100%' }}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <BusinessIcon sx={{ color: 'primary.main' }} />
                                            Customer & Goods
                                        </Typography>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    name="customerName"
                                                    label="Customer Name *"
                                                    value={formData.customerName}
                                                    onChange={handleChange}
                                                    error={errors.customerName}
                                                    helperText={errors.customerName ? 'Customer name is required' : ''}
                                                    placeholder="Enter customer name..."
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                            backgroundColor: '#f8fafc',
                                                            '&:hover': {
                                                                backgroundColor: '#f1f5f9'
                                                            }
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    name="goodsName"
                                                    label="Goods Name *"
                                                    value={formData.goodsName}
                                                    onChange={handleChange}
                                                    error={errors.goodsName}
                                                    helperText={errors.goodsName ? 'Goods name is required' : ''}
                                                    placeholder="Enter goods name..."
                                                    InputProps={{
                                                        startAdornment: <InventoryIcon sx={{ color: 'primary.main', mr: 1 }} />
                                                    }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                            backgroundColor: '#f8fafc',
                                                            '&:hover': {
                                                                backgroundColor: '#f1f5f9'
                                                            }
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    name="goodsDescription"
                                                    label="Goods Description"
                                                    value={formData.goodsDescription}
                                                    onChange={handleChange}
                                                    placeholder="Enter goods description..."
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                            backgroundColor: '#f8fafc',
                                                            '&:hover': {
                                                                backgroundColor: '#f1f5f9'
                                                            }
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Trip Name Display */}
                            {formData.tripName && (
                                <Grid item xs={12}>
                                    <Card sx={{ borderRadius: 2, border: '2px solid #667eea', backgroundColor: '#f0f4ff' }}>
                                        <CardContent>
                                            <Typography variant="h6" sx={{ mb: 1, color: '#667eea' }}>
                                                Generated Trip Name:
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 600, color: '#4c51bf' }}>
                                                {formData.tripName}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, backgroundColor: '#f8fafc', gap: 2 }}>
                <Button
                    onClick={onClose}
                    disabled={creatingTrip}
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        borderColor: '#cbd5e1',
                        color: '#64748b',
                        '&:hover': {
                            borderColor: '#94a3b8',
                            backgroundColor: '#f1f5f9'
                        }
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleResetForm}
                    disabled={creatingTrip}
                    color="secondary"
                    sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1.5
                    }}
                >
                    Reset
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={!isFormValid || creatingTrip}
                    onClick={handleSubmit}
                    sx={{
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                            color: 'white'
                        },
                        '&:disabled': {
                            background: '#e2e8f0',
                            color: '#94a3b8'
                        }
                    }}
                >
                    Create Trip
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NewTrip;
